import React, { useState, useEffect } from 'react';
import ResearchTickerInput from './ResearchTickerInput';
import ResearchFundamentalsTable from './ResearchFundamentalsTable';
import ResearchFinancialsTable from './ResearchFinancialsTable';
import ResearchEPSTrendsTable from './ResearchEPSTrendsTable';
import apiClient, { logResearchPageView } from '../../services/api';
import PriceChartModal from './PriceChartModal';
import MacroDashboard from '../StockAnalyzer/MacroDashboard/MacroDashboard';

function ResearchDashboard() {
  const [fundamentalsData, setFundamentalsData] = useState([]);
  const [financialsData, setFinancialsData] = useState(null);
  const [financialsYears, setFinancialsYears] = useState(5);
  const [financialsProcessing, setFinancialsProcessing] = useState(false);
  const [epsTrendsData, setEpsTrendsData] = useState([]);
  const [epsTrendsProcessing, setEpsTrendsProcessing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState([]);
  const [activeTab, setActiveTab] = useState('input');
  const [lastTickers, setLastTickers] = useState([]);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [chartTicker, setChartTicker] = useState(null);

  // Log which Research page/tab the user is viewing for User Intelligence
  // analytics (switching tabs triggers no other API call).
  useEffect(() => {
    logResearchPageView(activeTab);
  }, [activeTab]);

  const fetchFinancialsData = async (tickers, years) => {
    setFinancialsProcessing(true);
    try {
      const response = await apiClient.post('/research/financials/ratios/', {
        tickers,
        years
      });
      setFinancialsData(response.data);
    } catch (error) {
      console.error('Research financials fetch failed:', error);
      setErrors(prev => [...prev, 'Failed to fetch historical financials']);
    } finally {
      setFinancialsProcessing(false);
    }
  };

  const fetchEpsTrendsData = async (tickers) => {
    setEpsTrendsProcessing(true);
    try {
      const response = await apiClient.post('/research/eps-trends/', { tickers });
      setEpsTrendsData(response.data.eps_trends || []);
    } catch (error) {
      console.error('Research EPS trends fetch failed:', error);
      setErrors(prev => [...prev, 'Failed to fetch EPS trends']);
    } finally {
      setEpsTrendsProcessing(false);
    }
  };

  const handleFinancialsYearsChange = (years) => {
    setFinancialsYears(years);
    if (financialsData?.tickers?.length > 0) {
      fetchFinancialsData(financialsData.tickers, years);
    }
  };

  const handleAnalysisSubmit = async (tickers) => {
    setProcessing(true);
    setErrors([]);
    setLastTickers(tickers);

    try {
      const response = await apiClient.post('/research/fundamentals/', { tickers });
      setFundamentalsData(response.data.research_fundamentals || []);
      setActiveTab('fundamentals');

      // Also fetch financials and EPS trends data
      fetchFinancialsData(tickers, financialsYears);
      fetchEpsTrendsData(tickers);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Analysis failed';
      const errorDetails = error.response?.data?.details || {};

      let detailedErrors = [errorMessage];
      if (typeof errorDetails === 'object' && errorDetails !== null) {
        Object.entries(errorDetails).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach(msg => detailedErrors.push(`${field}: ${msg}`));
          } else {
            detailedErrors.push(`${field}: ${messages}`);
          }
        });
      }
      setErrors(detailedErrors);
    } finally {
      setProcessing(false);
    }
  };

  const handleTickerClick = (ticker) => {
    setChartTicker(ticker);
    setChartModalOpen(true);
  };

  const tabs = [
    { key: 'input', label: 'Analysis Input' },
    { key: 'fundamentals', label: 'Fundamentals' },
    { key: 'financials', label: 'Financials' },
    { key: 'epsTrends', label: 'EPS Trends' },
    { key: 'macro', label: 'Macro Dashboard' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'input':
        return (
          <ResearchTickerInput
            onSubmit={handleAnalysisSubmit}
            processing={processing}
            errors={errors}
          />
        );
      case 'fundamentals':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Fundamentals Analysis</h2>
              <span className="text-sm text-gray-400">
                {fundamentalsData.length} {fundamentalsData.length === 1 ? 'stock' : 'stocks'} analyzed
              </span>
            </div>
            <ResearchFundamentalsTable data={fundamentalsData} onTickerClick={handleTickerClick} />
          </div>
        );
      case 'financials':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Historical Financials</h2>
              {financialsProcessing && (
                <div className="flex items-center text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Loading financials...
                </div>
              )}
            </div>
            <ResearchFinancialsTable
              data={financialsData}
              years={financialsYears}
              onYearsChange={handleFinancialsYearsChange}
              onTickerClick={handleTickerClick}
            />
          </div>
        );
      case 'epsTrends':
        return (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">EPS Trends</h2>
              {epsTrendsProcessing && (
                <div className="flex items-center text-gray-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Loading EPS trends...
                </div>
              )}
            </div>
            <ResearchEPSTrendsTable data={epsTrendsData} onTickerClick={handleTickerClick} />
          </div>
        );
      case 'macro':
        return <MacroDashboard />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Research</h1>
          <p className="text-gray-400">
            Professional stock analysis and fundamentals research
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-gray-700 mb-6">
          <nav className="flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="bg-gray-800 rounded-lg p-6">
          {renderTabContent()}
        </div>

        {/* Processing Status Bar */}
        {processing && (
          <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing analysis...
            </div>
          </div>
        )}

        {/* Price Chart Modal */}
        <PriceChartModal
          isOpen={chartModalOpen}
          onClose={() => setChartModalOpen(false)}
          ticker={chartTicker}
        />
      </div>
    </div>
  );
}

export default ResearchDashboard;
